<script setup>
/**
 * Add-item form for a `whatsapp_template` follow-up item.
 *
 * Lets the user pick a Meta-approved template (from an official WhatsApp inbox)
 * and map each BODY parameter to a follow-up variable or static text. Also keeps
 * an editable plain-text fallback (pre-filled from the template body) that is sent
 * on non-official providers (WAHA/UazAPI) or inside the 24h window — see
 * FollowUpItemJob#official_template_send?.
 *
 * Emits `save` with the item payload, or `cancel`.
 */
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import {
  findComponentByType,
  getMediaType,
  hasMediaHeader,
  isValidMediaUrl,
  COMPONENT_TYPES,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  availableVariables: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['save', 'cancel']);

const store = useStore();
const { t } = useI18n();

const selectedInboxId = ref(null);
const selectedTemplateName = ref('');
const fallbackContent = ref('');
const mapping = ref([]);
const isFetching = ref(false);
// Media header (IMAGE/VIDEO/DOCUMENT): the file this sequence item sends. Static
// by design — a sequence points at one document, so there is no variable to map.
const mediaUrl = ref('');
const mediaName = ref('');

// Bare variable names (contact_name) for the mapping dropdown.
const variableOptions = computed(() =>
  props.availableVariables.map(v => v.replace(/[{}]/g, ''))
);

const whatsappInboxes = computed(() =>
  (store.getters['inboxes/getInboxes'] || []).filter(
    inbox => inbox.channel_type === 'Channel::Whatsapp'
  )
);

const hasWhatsappInbox = computed(() => whatsappInboxes.value.length > 0);

const approvedTemplates = computed(() =>
  (store.getters['whatsappTemplates/getTemplates'] || []).filter(
    tpl => (tpl.status || '').toLowerCase() === 'approved'
  )
);

const selectedTemplate = computed(
  () =>
    approvedTemplates.value.find(
      tpl => tpl.name === selectedTemplateName.value
    ) || null
);

const bodyText = computed(() => {
  if (!selectedTemplate.value) return '';
  const body = findComponentByType(
    selectedTemplate.value,
    COMPONENT_TYPES.BODY
  );
  return body?.text || '';
});

// Meta requires the header parameter whenever the template declares a media
// header — a send without it is rejected, not delivered without the file.
const templateHasMediaHeader = computed(() =>
  selectedTemplate.value ? hasMediaHeader(selectedTemplate.value) : false
);

const mediaType = computed(() =>
  templateHasMediaHeader.value ? getMediaType(selectedTemplate.value) : ''
);

// Only DOCUMENT headers carry a filename (it is what the recipient sees).
const isDocumentHeader = computed(() => mediaType.value === 'document');

const mediaTypeLabel = computed(() => {
  const format = findComponentByType(
    selectedTemplate.value || {},
    COMPONENT_TYPES.HEADER
  )?.format;
  return format ? format.charAt(0) + format.slice(1).toLowerCase() : '';
});

const showMediaUrlError = computed(
  () => !!mediaUrl.value && !isValidMediaUrl(mediaUrl.value)
);

const extractPlaceholders = text =>
  (text.match(/{{\s*([^}]+?)\s*}}/g) || []).map(m => m.replace(/[{}\s]/g, ''));

// Display a variable name wrapped in braces without putting literal "}}" inside a
// template mustache (which would break the Vue parser).
const wrapVar = name => `{{${name}}}`;

// Rebuild the fallback text by substituting each template placeholder with the
// mapped follow-up variable ({{var}}) or the static text.
const buildFallback = () => {
  let text = bodyText.value;
  mapping.value.forEach(entry => {
    const replacement =
      entry.type === 'variable' ? `{{${entry.value}}}` : entry.value;
    text = text.replace(
      new RegExp(`{{\\s*${entry.placeholder}\\s*}}`),
      replacement
    );
  });
  return text;
};

const fetchTemplates = async inboxId => {
  if (!inboxId) return;
  isFetching.value = true;
  try {
    await store.dispatch('whatsappTemplates/fetchTemplates', { inboxId });
  } catch {
    useAlert(t('FOLLOW_UP_ITEMS.WHATSAPP_TEMPLATE.FETCH_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

watch(selectedInboxId, async id => {
  selectedTemplateName.value = '';
  mapping.value = [];
  fallbackContent.value = '';
  mediaUrl.value = '';
  mediaName.value = '';
  await fetchTemplates(id);
});

watch(selectedTemplate, tpl => {
  // The file belongs to the template that was picked; carrying it across a
  // switch would silently attach the wrong document.
  mediaUrl.value = '';
  mediaName.value = '';
  if (!tpl) {
    mapping.value = [];
    fallbackContent.value = '';
    return;
  }
  const placeholders = extractPlaceholders(bodyText.value);
  mapping.value = placeholders.map(placeholder => ({
    placeholder,
    type: 'variable',
    value: variableOptions.value[0] || '',
  }));
  fallbackContent.value = buildFallback();
});

const regenerateFallback = () => {
  fallbackContent.value = buildFallback();
};

const canSave = computed(
  () =>
    !!selectedTemplate.value &&
    fallbackContent.value.trim().length > 0 &&
    // Saving a media-header item without the file would produce an item that
    // looks configured and can never be delivered.
    (!templateHasMediaHeader.value || isValidMediaUrl(mediaUrl.value)) &&
    mapping.value.every(
      entry =>
        entry.type === 'text' || (entry.value && entry.value.trim().length > 0)
    )
);

const buildMapping = () => {
  const built = {
    body: mapping.value.map(entry => ({
      type: entry.type,
      value: entry.value,
    })),
  };
  if (!templateHasMediaHeader.value) return built;

  built.header = { media_url: mediaUrl.value, media_type: mediaType.value };
  if (mediaName.value) built.header.media_name = mediaName.value;
  return built;
};

const onSave = () => {
  if (!canSave.value) return;
  emit('save', {
    item_type: 'whatsapp_template',
    whatsapp_template_name: selectedTemplate.value.name,
    whatsapp_template_language: selectedTemplate.value.language,
    whatsapp_template_namespace: selectedTemplate.value.namespace || null,
    whatsapp_template_mapping: buildMapping(),
    content: fallbackContent.value,
  });
};

onMounted(() => {
  store.dispatch('inboxes/get');
  if (whatsappInboxes.value.length === 1) {
    selectedInboxId.value = whatsappInboxes.value[0].id;
  }
});
</script>

<template>
  <div class="space-y-3">
    <!-- No official WhatsApp inbox -->
    <div
      v-if="!hasWhatsappInbox"
      class="rounded-lg border border-n-amber-6 bg-n-amber-3 px-3 py-2.5 text-xs text-n-amber-11 flex items-start gap-2"
    >
      <span class="i-lucide-info size-4 mt-0.5 flex-shrink-0" />
      <span>{{
        t('FOLLOW_UP_ITEMS.WHATSAPP_TEMPLATE.NO_OFFICIAL_INBOX')
      }}</span>
    </div>

    <template v-else>
      <!-- Inbox selector -->
      <div v-if="whatsappInboxes.length > 1">
        <label
          class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1"
        >
          {{ t('FOLLOW_UP_ITEMS.WHATSAPP_TEMPLATE.INBOX_LABEL') }}
        </label>
        <select
          v-model="selectedInboxId"
          class="w-full !mb-0 appearance-none !bg-none border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200"
        >
          <option :value="null" disabled>
            {{ t('FOLLOW_UP_ITEMS.WHATSAPP_TEMPLATE.INBOX_PLACEHOLDER') }}
          </option>
          <option
            v-for="inbox in whatsappInboxes"
            :key="inbox.id"
            :value="inbox.id"
          >
            {{ inbox.name }}
          </option>
        </select>
      </div>

      <!-- Template selector -->
      <div>
        <label
          class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1"
        >
          {{ t('FOLLOW_UP_ITEMS.WHATSAPP_TEMPLATE.TEMPLATE_LABEL') }}
        </label>
        <select
          v-model="selectedTemplateName"
          :disabled="!selectedInboxId || isFetching"
          name="whatsapp-template-select"
          :aria-label="t('FOLLOW_UP_ITEMS.WHATSAPP_TEMPLATE.TEMPLATE_LABEL')"
          class="w-full !mb-0 appearance-none !bg-none border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 disabled:opacity-50"
        >
          <option value="" disabled>
            {{
              isFetching
                ? t('FOLLOW_UP_ITEMS.WHATSAPP_TEMPLATE.LOADING')
                : t('FOLLOW_UP_ITEMS.WHATSAPP_TEMPLATE.TEMPLATE_PLACEHOLDER')
            }}
          </option>
          <option
            v-for="tpl in approvedTemplates"
            :key="`${tpl.name}-${tpl.language}`"
            :value="tpl.name"
          >
            {{ tpl.name }} ({{ tpl.language }})
          </option>
        </select>
        <p
          v-if="
            selectedInboxId && !isFetching && approvedTemplates.length === 0
          "
          class="mt-1 text-xs text-slate-500 dark:text-slate-400"
        >
          {{ t('FOLLOW_UP_ITEMS.WHATSAPP_TEMPLATE.NO_APPROVED') }}
        </p>
      </div>

      <!-- Media header: the file Meta requires for IMAGE/VIDEO/DOCUMENT headers -->
      <div v-if="templateHasMediaHeader" class="space-y-2">
        <label
          class="block text-xs font-medium text-slate-600 dark:text-slate-400"
        >
          {{
            t('WHATSAPP_TEMPLATES.PARSER.MEDIA_HEADER_LABEL', {
              type: mediaTypeLabel,
            })
          }}
        </label>
        <NextInput
          v-model="mediaUrl"
          name="template-media-url"
          type="url"
          :placeholder="
            t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_LABEL', {
              type: mediaTypeLabel,
            })
          "
          :message="
            showMediaUrlError
              ? t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_INVALID')
              : t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_HINT')
          "
          :message-type="showMediaUrlError ? 'error' : 'info'"
        />
        <NextInput
          v-if="isDocumentHeader"
          v-model="mediaName"
          name="template-media-name"
          type="text"
          :placeholder="
            t('WHATSAPP_TEMPLATES.PARSER.DOCUMENT_NAME_PLACEHOLDER')
          "
        />
      </div>

      <!-- Parameter mapping -->
      <div v-if="selectedTemplate && mapping.length" class="space-y-2">
        <label
          class="block text-xs font-medium text-slate-600 dark:text-slate-400"
        >
          {{ t('FOLLOW_UP_ITEMS.WHATSAPP_TEMPLATE.MAPPING_LABEL') }}
        </label>
        <div
          v-for="entry in mapping"
          :key="entry.placeholder"
          class="flex items-center gap-2"
        >
          <span
            class="text-xs font-mono text-slate-500 dark:text-slate-400 w-10 flex-shrink-0"
          >
            {{ wrapVar(entry.placeholder) }}
          </span>
          <select
            v-model="entry.type"
            :aria-label="t('FOLLOW_UP_ITEMS.WHATSAPP_TEMPLATE.PARAM_TYPE')"
            class="!mb-0 appearance-none !bg-none border border-slate-300 dark:border-slate-600 rounded-lg px-2 py-1.5 text-xs bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200"
            @change="regenerateFallback"
          >
            <option value="variable">
              {{ t('FOLLOW_UP_ITEMS.WHATSAPP_TEMPLATE.PARAM_VARIABLE') }}
            </option>
            <option value="text">
              {{ t('FOLLOW_UP_ITEMS.WHATSAPP_TEMPLATE.PARAM_TEXT') }}
            </option>
          </select>
          <select
            v-if="entry.type === 'variable'"
            v-model="entry.value"
            :aria-label="t('FOLLOW_UP_ITEMS.WHATSAPP_TEMPLATE.PARAM_VARIABLE')"
            class="flex-1 !mb-0 appearance-none !bg-none border border-slate-300 dark:border-slate-600 rounded-lg px-2 py-1.5 text-xs bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200"
            @change="regenerateFallback"
          >
            <option v-for="opt in variableOptions" :key="opt" :value="opt">
              {{ wrapVar(opt) }}
            </option>
          </select>
          <input
            v-else
            v-model="entry.value"
            type="text"
            :aria-label="t('FOLLOW_UP_ITEMS.WHATSAPP_TEMPLATE.PARAM_TEXT')"
            :placeholder="
              t('FOLLOW_UP_ITEMS.WHATSAPP_TEMPLATE.PARAM_TEXT_PLACEHOLDER')
            "
            class="flex-1 !mb-0 border border-slate-300 dark:border-slate-600 rounded-lg px-2 py-1.5 text-xs bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200"
            @input="regenerateFallback"
          />
        </div>
      </div>

      <!-- Fallback text (non-official / in-window) -->
      <div v-if="selectedTemplate">
        <div class="flex items-center justify-between mb-1">
          <label
            class="block text-xs font-medium text-slate-600 dark:text-slate-400"
          >
            {{ t('FOLLOW_UP_ITEMS.WHATSAPP_TEMPLATE.FALLBACK_LABEL') }}
          </label>
          <button
            type="button"
            class="text-[11px] text-n-brand hover:underline"
            @click="regenerateFallback"
          >
            {{ t('FOLLOW_UP_ITEMS.WHATSAPP_TEMPLATE.REGENERATE') }}
          </button>
        </div>
        <textarea
          v-model="fallbackContent"
          rows="3"
          name="whatsapp-template-fallback"
          :aria-label="t('FOLLOW_UP_ITEMS.WHATSAPP_TEMPLATE.FALLBACK_LABEL')"
          class="w-full !mb-0 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 resize-none"
        />
        <p class="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
          {{ t('FOLLOW_UP_ITEMS.WHATSAPP_TEMPLATE.FALLBACK_HINT') }}
        </p>
      </div>

      <!-- Actions -->
      <div class="flex gap-2 mt-3">
        <NextButton
          size="sm"
          variant="faded"
          color="slate"
          class="flex-1"
          @click="emit('cancel')"
        >
          {{ t('FOLLOW_UP_ITEMS.MODAL.CANCEL') }}
        </NextButton>
        <NextButton
          size="sm"
          variant="solid"
          color="blue"
          class="flex-1"
          :disabled="!canSave"
          @click="onSave"
        >
          {{ t('FOLLOW_UP_ITEMS.MODAL.CREATE') }}
        </NextButton>
      </div>
    </template>
  </div>
</template>
